<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { onBeforeMount, onMounted, ref, computed } from "vue"
import {
    useDocumentTitle,
    useScopeFavicon,
    useFetchWrapper,
    useHomeysIframe,
} from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import { useEventBus } from "@vueuse/core"
import { useToast } from "primevue/usetoast"
import { useRouter } from "vue-router"
import type { NotificationEvent, NotificationEventTask } from "@types"

// ================================
// COMPOSABLES
// ================================
const authStore = useAuthStore()
const configStore = useConfigStore()
const router = useRouter()
const { isHomeysIframe } = useHomeysIframe()

useDocumentTitle()
useScopeFavicon()

// ================================
// STATE
// ================================
const app = ref<HTMLElement | null>(null)

/** Scope (et auth iframe si besoin) chargés → contenu principal affichable */
const scopeReady = ref(false)

/** Dernière hauteur envoyée au parent (iframe) pour éviter envois inutiles */
const lastSentEmbedHeight = ref(0)

// ================================
// COMPUTED
// ================================
/** Splash plein écran en mode standalone tant que le scope n’est pas prêt */
const getShowSplashScreen = computed(
    () => !isHomeysIframe.value && !scopeReady.value
)
/** Chargement minimal en mode iframe widget */
const getShowLoadingApp = computed(
    () => isHomeysIframe.value && !scopeReady.value
)
/** Erreur lors du chargement des données initiales */
const getHasInitialDataError = computed(
    () =>
        !fetchingInitialData.isFetching.value &&
        fetchingInitialData.hasError.value
)

// ================================
// CHARGEMENT DONNÉES INITIALES (user, scope, graphList)
// ================================
const fetchingInitialData = useFetchWrapper(async () => {
    if (authStore.isLogged && !authStore.userId) {
        await authStore.fetchUser()
    }
})

/**
 * Iframe : scope puis user si session.
 */
async function runBootstrapHomeysIframe(): Promise<void> {
    await configStore.fetchScope()

    if (authStore.isLogged) {
        await fetchingInitialData.fetch()
    }
}

/**
 * Parcours standard : attendre le scope (splash), puis données initiales si connecté
 */
async function runBootstrapStandalone(): Promise<void> {
    await configStore.fetchScope()
    if (!authStore.isLogged) {
        return
    }
    await fetchingInitialData.fetch()
}

// ================================
// LIFECYCLE
// ================================
onBeforeMount(async () => {
    await router.isReady()
    if (isHomeysIframe.value) {
        await runBootstrapHomeysIframe()
    } else {
        await runBootstrapStandalone()
    }
    scopeReady.value = true
})

// ================================
// INTERACTIONS / IFRAME
// ================================
const EMBED_HEIGHT_THRESHOLD = 2

/**
 * Envoie la hauteur du contenu (`div.app`) au parent (iframe).
 * Mesure l’élément racine du contenu, pas document/body, pour éviter une boucle de hauteur.
 */
function setupHomeysIframeResizeObserver(): void {
    const resizeObserver = new ResizeObserver(() => {
        if (!isHomeysIframe.value || !app.value) return
        const contentHeight = Math.max(
            app.value.scrollHeight,
            app.value.offsetHeight
        )
        if (
            contentHeight > 0 &&
            Math.abs(contentHeight - lastSentEmbedHeight.value) >=
                EMBED_HEIGHT_THRESHOLD
        ) {
            lastSentEmbedHeight.value = contentHeight
            window.parent.postMessage(
                { type: "update:clientHeight", value: contentHeight },
                "*"
            )
        }
    })
    if (app.value) resizeObserver.observe(app.value)
}

onMounted(() => {
    setupHomeysIframeResizeObserver()
})

// ================================
// NOTIFICATIONS (toast)
// ================================
const toast = useToast()
const bus = useEventBus<NotificationEvent>("notification")
const busTask = useEventBus<NotificationEventTask>("task")

bus.on((data) => {
    if (data) {
        toast.add({
            group: "notifications",
            severity: data.severity,
            summary: data.title,
            detail: data.message,
            life: data.life || 5000,
        })
    }
})
busTask.on((data) => {
    if (data) {
        const notification = {
            group: "task",
            severity: data.severity,
            summary: data.title,
            detail: data.message,
            closable: false,
        }
        toast.add(notification)
        if (data.callback) data.callback(notification)
    }
})
</script>

<template>
    <!-- ================================ -->
    <!-- RACINE APPLICATION -->
    <!-- ================================ -->
    <div
        ref="app"
        class="app"
        :style="{
            background: !isHomeysIframe
                ? 'linear-gradient(243deg, #edf4fe 0%, #eff3f8 100%)'
                : undefined,
        }"
    >
        <template v-if="getShowSplashScreen">
            <SplashScreen />
        </template>
        <template v-else-if="getShowLoadingApp">
            <LoadingApp class="page" />
        </template>
        <template v-else>
            <BaseToast group="notifications" />
            <BaseToast group="task">
                <template #message="slotProps">
                    <div class="flex gap-2 items-center flex-auto">
                        <i class="pi pi-spin pi-spinner" />
                        <div class="flex flex-col gap-2">
                            <h5>{{ slotProps.message.summary }}</h5>
                            <p class="text-sm">
                                {{ slotProps.message.detail }}
                            </p>
                        </div>
                    </div>
                </template>
            </BaseToast>
            <TaskToast />
            <SupportButton v-if="!isHomeysIframe" />
            <div v-if="getHasInitialDataError">
                <BaseMessage>
                    Une erreur est survenue pour charger la page, veuillez
                    réessayer plus tard. Notre équipe technique a été notifiée.
                </BaseMessage>
            </div>
            <template v-else>
                <RouterView />
            </template>
        </template>
    </div>
</template>
